import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { BlobShipMark } from "./BlobShipMark";
import { OPSBELT_APP_URL } from "../lib/env";
import type { Me } from "../lib/types";

/** Feather/lucide-style icons inlined to match the brand comp exactly. */
const I = {
  overview: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  connectors: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  destinations: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8v13H3V8" /><path d="M1 3h22v5H1z" /><path d="M10 12h4" />
    </svg>
  ),
  keys: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.1 2.1-2.3-.6-.6-2.3z" />
    </svg>
  ),
  audit: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h8" />
    </svg>
  ),
  billing: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
    </svg>
  ),
  external: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" /><path d="M10 14 21 3" />
    </svg>
  ),
};

type NavItem = { to: string; label: string; icon: ReactNode; external?: string };

/** Nav, gated by role. Billing isn't a BlobShip page — for owners/admins it's an
 * external link to the OpsBelt admin (manage products), opening in a new tab. */
function sectionsFor(role: string | undefined): { label: string; items: NavItem[] }[] {
  const canManageBilling = role === "owner" || role === "admin";
  const account: NavItem[] = [{ to: "/audit", label: "Activity", icon: I.audit }];
  if (canManageBilling) {
    account.push({
      to: "ext-billing",
      label: "Billing",
      icon: I.billing,
      external: `${OPSBELT_APP_URL}/apps`,
    });
  }
  return [
    { label: "Workspace", items: [{ to: "/", label: "Overview", icon: I.overview }] },
    {
      label: "BlobShip",
      items: [
        { to: "/connectors", label: "Connections", icon: I.connectors },
        { to: "/destinations", label: "Destinations", icon: I.destinations },
        { to: "/keys", label: "Upload keys", icon: I.keys },
      ],
    },
    { label: "Account", items: account },
  ];
}

export function Sidebar({ me, onSignOut }: { me: Me | undefined; onSignOut: () => void }) {
  const initials = (me?.user.name || me?.user.email || "?")
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <aside className="flex h-full w-[248px] shrink-0 flex-col border-r border-surface-200 bg-surface-50">
      {/* brand lockup — sits directly on the sidebar, no nested card */}
      <div className="flex h-[60px] items-center gap-[11px] border-b border-surface-200 px-[18px]">
        <BlobShipMark size={28} className="shrink-0 text-accent" />
        <div className="min-w-0 leading-[1.15]">
          <div className="font-display text-[15px] font-bold tracking-tight text-ink">
            Blob<span className="text-accent">Ship</span>
          </div>
          <div className="truncate font-mono text-[10px] text-ink-subtle">
            {me?.org.name ?? "Your workspace"}
          </div>
        </div>
      </div>

      {/* nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3.5">
        {sectionsFor(me?.org.role).map((section) => (
          <div key={section.label}>
            <div className="px-2.5 pb-2 pt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-subtle first:pt-1.5">
              {section.label}
            </div>
            {section.items.map((item) =>
              item.external ? (
                <a
                  key={item.to}
                  href={item.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-0.5 flex items-center gap-[11px] rounded-[9px] px-2.5 py-2.5 text-[13.5px] font-medium text-ink-muted transition-colors hover:bg-surface-100 hover:text-ink [&>svg]:text-ink-subtle"
                >
                  {item.icon}
                  <span>{item.label}</span>
                  <span className="ml-auto text-ink-subtle">{I.external}</span>
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `mb-0.5 flex items-center gap-[11px] rounded-[9px] px-2.5 py-2.5 text-[13.5px] font-medium transition-colors ${
                      isActive
                        ? "bg-surface-100 text-ink shadow-[inset_2px_0_0_#7C6CFF] [&_svg]:text-accent"
                        : "text-ink-muted hover:bg-surface-100 hover:text-ink [&_svg]:text-ink-subtle"
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ),
            )}
          </div>
        ))}
      </nav>

      {/* user */}
      <div className="border-t border-surface-200 p-3">
        <div className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2">
          <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent to-[#5B4BE0] text-[12px] font-bold text-white">
            {initials || "U"}
          </span>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-semibold text-ink">
              {me?.user.name || me?.user.email || "—"}
            </div>
            <div className="font-mono text-[10px] text-ink-subtle">
              {me?.org.role ?? ""}
            </div>
          </div>
          <button
            onClick={onSignOut}
            className="ml-auto text-ink-subtle hover:text-ink"
            title="Sign out"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
